import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { isBefore, startOfDay } from 'date-fns';
import { Button, CardView, rem, Spacer } from 'design-system';
import Amount from 'design-system/Amount';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconStatusPaused from 'design-system/icons/status/IconStatusPaused';
import IconStatusPending from 'design-system/icons/status/IconStatusPending';
import Text from 'design-system/Text';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { formatDateForItem } from 'utils/formatting';
import { skipAutoInvestPayment, unskipAutoInvestPayment } from '../actions/autoinvest';
import { blockInvestActionOnWeb } from '../webGate';
const UpcomingAutoinvestCard = ({ activity, containerStyle }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [, , requesting, fetch] = useFetchOld(muteErrorAndResult);
    const isSkipped = activity.status === 'skipped';
    const canUnskip = useMemo(() => isSkipped && isBefore(startOfDay(new Date()), new Date(activity.timestamp)), [isSkipped, activity.timestamp]);
    const handleSkipPress = useCallback(() => {
        if (blockInvestActionOnWeb('Autoinvest'))
            return;
        if (activity.paymentCollectionEta) {
            if (isSkipped) {
                fetch(unskipAutoInvestPayment(activity.autoInvestPlanId, activity.accountId, activity.paymentCollectionEta, activity.id));
            }
            else {
                fetch(skipAutoInvestPayment(activity.autoInvestPlanId, activity.accountId, activity.paymentCollectionEta, activity.id));
            }
        }
    }, [isSkipped, activity.autoInvestPlanId, activity.accountId, activity.id, activity.paymentCollectionEta]);
    return (<CardView noMarginTop withPadding noMarginBottom style={[containerStyle && { borderRadius: 0 }, containerStyle]}>
      <View style={styles.row}>
        <View style={styles.iconContainer}>
          <IconRocket colorKey="brand"/>
        </View>
        <Spacer.W16 />
        <View style={styles.flex}>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16>Autoinvest</Text>
              <View style={styles.row}>
                {isSkipped ? (<>
                    <IconStatusPaused width={16} height={16} colorKey="secondary" color={colors.cards.onDark} bgColor={colors.elements.secondary}/>
                    <Spacer w={8}/>
                    <Text TextThin-14 Secondary>
                      Skipped
                    </Text>
                  </>) : (<>
                    <IconStatusPending width={16} height={16} color={colors.cards.onDark} bgColor={colors.elements.statusInformational}/>
                    <Spacer w={8}/>
                    <Text TextThin-14 StatusInformational numberOfLines={1}>
                      Upcoming
                    </Text>
                  </>)}
              </View>
            </View>
            <View style={styles.rightContent}>
              <Amount showSign Numbers-16 value={activity.amount} style={isSkipped ? styles.skipped : undefined} color={isSkipped ? colors.text.tertiary : colors.text.income}/>
              <Text TextThin-14 Secondary>
                {formatDateForItem(new Date(activity.timestamp))}
              </Text>
            </View>
          </View>
        </View>
      </View>
      {!isSkipped || canUnskip ? (<>
          <Spacer.H16 />
          <Spacer.H8 />
          <Button square brandReversed isFetching={requesting} title={isSkipped ? 'Undo skip' : 'Skip this payment'} onPress={handleSkipPress}/>
        </>) : null}
    </CardView>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    iconContainer: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        borderRadius: rem(42),
        alignItems: 'center',
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.brandLight,
    },
    rightContent: {
        alignItems: 'flex-end',
    },
    skipped: {
        textDecorationStyle: 'solid',
        textDecorationLine: 'line-through',
    },
}));
export default UpcomingAutoinvestCard;
